<template>
  <div v-if="anchors.length" ref="tabsContainer" class="scrollbar-none">
    <RevTabs
      class="isolate"
      :label="i18n(translations.tabsLabel)"
      variant="flexible"
    >
      <RevTabItem
        v-for="tab in anchors"
        :key="tab.id"
        :active="activeAnchor === tab.id"
        :label="tab.label"
        @click="scrollToAnchor(tab.id)"
      />
    </RevTabs>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import type { Anchor } from '../../../models/productComparison'

import translations from './Tabs.translations'

const { anchors } = defineProps<{
  anchors: Anchor[]
}>()

const i18n = useI18n()
const tabsContainer = useTemplateRef<HTMLElement>('tabsContainer')
const activeAnchor = ref<string | null>(null)

// When the active section changes, scroll the corresponding tab into view
// within the tab bar's horizontal scroll area (without affecting page scroll).
watch(activeAnchor, (id) => {
  if (!id) return
  const index = anchors.findIndex((t) => t.id === id)
  if (index === -1) return

  const nav = tabsContainer.value?.querySelector('nav')
  // RevTabs renders tab items as direct children of the [role="tablist"] element.
  const item = tabsContainer.value?.querySelector<HTMLElement>(
    `[role="tablist"] > :nth-child(${index + 1})`,
  )
  if (!nav || !item) return

  const { offsetLeft, offsetWidth } = item
  const { scrollLeft, offsetWidth: navWidth } = nav
  const padding = 16

  if (offsetLeft < scrollLeft) {
    nav.scrollTo({ left: offsetLeft - padding, behavior: 'smooth' })
  } else if (offsetLeft + offsetWidth > scrollLeft + navWidth) {
    nav.scrollTo({
      left: offsetLeft + offsetWidth - navWidth + padding,
      behavior: 'smooth',
    })
  }
})

function scrollToAnchor(id: string) {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

let anchorObserver: IntersectionObserver | null = null

function observeAnchors() {
  anchorObserver?.disconnect()
  if (!anchors.length) return

  // A heading is "active" once its top reaches the bottom of the sticky
  // header. The sticky height matches the scroll-mt-* on the headings (see
  // TableContent.vue); +4 absorbs sub-pixel landings of scrollIntoView at
  // end-of-scroll, otherwise the previous tab can linger active for 1–2 px.
  const stickyHeight = window.matchMedia('(min-width: 768px)').matches
    ? 202
    : 196
  const activationLine = stickyHeight + 4

  // Active = the last heading whose top has crossed the activation line.
  // Anchors are in DOM order, so we can stop at the first one still below.
  // Default to the first anchor when none have crossed (top of page).
  function updateActive() {
    let active = anchors[0]?.id ?? null
    for (const { id } of anchors) {
      const el = document.getElementById(id)
      if (!el) continue
      if (el.getBoundingClientRect().top <= activationLine) {
        active = id
      } else {
        break
      }
    }
    activeAnchor.value = active
  }

  // The shrunken root's top sits at y = activationLine; threshold 1.0 fires
  // as a heading transitions between fully-inside and partially-inside the
  // root, i.e. as its top crosses that line in either direction.
  anchorObserver = new IntersectionObserver(updateActive, {
    rootMargin: `-${activationLine}px 0px 0px 0px`,
    threshold: 1.0,
  })

  for (const { id } of anchors) {
    const el = document.getElementById(id)
    if (el) anchorObserver.observe(el)
  }

  updateActive()
}

onMounted(observeAnchors)
onBeforeUnmount(() => anchorObserver?.disconnect())
watch(() => anchors, observeAnchors)
</script>

<style scoped>
/* RevTabs flexible variant adds a bottom margin below its border — hide the scrollbar
   without losing scroll functionality when this component is used in a sticky header. */
.scrollbar-none :deep(nav) {
  scrollbar-width: none;
}
.scrollbar-none :deep(nav::-webkit-scrollbar) {
  display: none;
}
</style>
